import { useEffect, useRef, useState, type ReactElement } from "react";
import type { CatalogAction, FormFieldSpec } from "../types.js";
import { FormRenderer } from "./FormRenderer.js";
import { validateForm, serializeFormValues } from "../lib/display.js";

interface Props {
  action: CatalogAction;
  stageId: string;
  stageProtected: boolean;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

function actionFields(action: CatalogAction): FormFieldSpec[] {
  if (!action.form) return [];
  if (typeof action.form === "string") return []; // named forms are resolved in the review view (Phase 2)
  return action.form.inline;
}

const needsStageConfirm = (action: CatalogAction, stageProtected: boolean): boolean =>
  stageProtected && (action.danger === "medium" || action.danger === "high");

/** Confirmation dialog for an action: form fields + confirm text + type-stage-id gate. */
export function ActionDialog({ action, stageId, stageProtected, onSubmit, onClose }: Props): ReactElement {
  const fields = actionFields(action);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [confirmText, setConfirmText] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const firstRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const confirmRequired = needsStageConfirm(action, stageProtected);
  const confirmOk = !confirmRequired || confirmText === stageId;

  async function submit(): Promise<void> {
    const result = validateForm(fields, values);
    setErrors(result.errors);
    if (!result.ok || !confirmOk) return;
    const body = serializeFormValues(fields, values);
    if (confirmRequired) body.confirmStage = stageId;
    setSubmitting(true);
    setServerError(null);
    try {
      await onSubmit(body);
      onClose();
    } catch (e) {
      setServerError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="action-title" className="dialog">
      <h2 id="action-title">{action.label ?? action.id}</h2>
      {action.description ? <p className="field-hint">{action.description}</p> : null}
      {action.confirm ? <p className="confirm-text">{action.confirm}</p> : null}
      {fields.some((f) => f.required) ? (
        <p className="field-hint">
          Fields marked <span aria-hidden="true">*</span>
          <span className="sr-only">with an asterisk</span> are required.
        </p>
      ) : null}

      <FormRenderer fields={fields} values={values} errors={errors} onChange={(id, v) => setValues((p) => ({ ...p, [id]: v }))} />

      {confirmRequired ? (
        <div className="field">
          <label htmlFor="confirm-stage">
            This is a protected stage. Type <strong>{stageId}</strong> to confirm.
          </label>
          <p className="field-hint">This is a safety check so changes to live members are never made by accident.</p>
          <input
            id="confirm-stage"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
          />
        </div>
      ) : null}

      {serverError ? (
        <p role="alert" className="field-error">
          {serverError}
        </p>
      ) : null}

      <div className="dialog-actions">
        <button ref={firstRef} type="button" onClick={onClose}>
          Cancel
        </button>
        <button type="button" disabled={!confirmOk || submitting} onClick={() => void submit()}>
          {submitting ? "Working…" : "Confirm"}
        </button>
      </div>
    </div>
  );
}

export { needsStageConfirm };
